/**
 * Forecast, the simple view. No groups to open or close: parts that need something (order now, ship
 * sooner, late PO) are listed flat, soonest to run out first, each tagged with its product line;
 * everything else is one line of product-line chips at the bottom (click one to list its parts).
 * The status strip filters to any one status. "More detail" switches to the full planner table.
 */
import { useMemo, useState } from "react";
import { CategoryTabs } from "../components/CategoryTabs.tsx";
import { ExportButton, ListBar } from "../components/ListBar.tsx";
import { useSearchParams } from "react-router-dom";
import { CATEGORY_LABEL, isYards } from "../lib/categories.ts";
import { downloadCsv } from "../lib/csv.ts";
import { partName, seriesLabel } from "../lib/skuNames.ts";
import { addDays, fmt, isTopUp, needs, runsOut, useSupply, type Row } from "../lib/supply.ts";
import { frameSortRank } from "../data/frameOrder.ts";
import { coverGroupRank, coverSortRank } from "../data/coverOrder.ts";
import { baseSortRank } from "../data/baseOrder.ts";
import { usePanel } from "../components/DetailPanel.tsx";
import { forecastTriple, OrderNum, StockNum, stockStory, UseNum } from "../components/StockTimeline.tsx";
import { ForecastDetail } from "./ForecastPage.tsx";
import { t } from "../lib/i18n.tsx";

const TYPES = ["Frame", "Cover", "Base", "Fabric", "Trim", "ProtectiveCover"] as const;
type FType = (typeof TYPES)[number];

type StatusKey = "order" | "sooner" | "late" | "topup" | "over";
type Status = { key: StatusKey; label: string; cls: string };

function statusOf(r: Row, today: string): Status[] {
  const s: Status[] = [];
  if (needs(r) && runsOut(r)) s.push({ key: "order", label: t("Order now"), cls: "order" });
  if ((r.days_exposed ?? 0) > 0) s.push({ key: "sooner", label: t("Ship sooner"), cls: "speed" });
  if ((r.arrivals ?? []).some((a) => a.date != null && a.date < today)) s.push({ key: "late", label: t("Late PO"), cls: "late" });
  if (isTopUp(r)) s.push({ key: "topup", label: t("Top-up"), cls: "topup" });
  // The other end of the same forecasting problem: more than a year of stock on the shelf.
  if (!s.length && (r.days_cover_on_hand ?? 0) > 365) s.push({ key: "over", label: t("Over a year of stock"), cls: "over" });
  return s;
}
const isProblem = (st: Status[]) => st.some((x) => x.key === "order" || x.key === "sooner" || x.key === "late");
const onOrder = (r: Row) => (r.supply.incoming_within_lead ?? 0) + (r.supply.incoming_after_lead ?? 0) || (r.supply.comp_po ?? 0);
const isQuiet = (r: Row) => (r.supply.comp_available ?? 0) === 0 && onOrder(r) === 0 && (r.demand?.forecasted ?? 0) === 0;

const COVER_FAMILY: Record<string, string> = { Olefin: "Solstice covers", Pacifica: "Pacifica Marine covers", Sunbrella: "Sunbrella covers" };

function groupOf(r: Row): { key: string; label: string; rank: number } {
  if (r.item_type === "Frame") {
    const code = r.itemid.match(/^[A-Z]+/)?.[0] ?? r.itemid;
    return { key: code, label: seriesLabel(code), rank: 0 };
  }
  if (r.item_type === "Cover") {
    const fam = r.cover_subtype ?? "";
    return { key: fam || "other", label: t(COVER_FAMILY[fam] ?? "Other covers"), rank: coverGroupRank(r.cover_subtype) };
  }
  if (r.item_type === "Base") {
    const code = r.itemid.match(/^[A-Z]+/)?.[0] ?? "Other";
    return { key: code, label: t("{code} bases", { code }), rank: 0 };
  }
  return { key: "all", label: t(CATEGORY_LABEL[r.item_type] ?? r.item_type), rank: 0 };
}

function rowRank(r: Row): number {
  if (r.item_type === "Frame") return frameSortRank(r.itemid);
  if (r.item_type === "Cover") return coverSortRank(r.itemid, r.cover_subtype);
  if (r.item_type === "Base") return baseSortRank(r.itemid);
  return -(r.demand?.forecasted ?? 0); // fabric, trim, protective covers: busiest first
}

export function ForecastPage() {
  const [params, setParams] = useSearchParams();
  const detail = params.get("view") === "detail";
  const setDetail = (on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.set("view", "detail");
    else next.delete("view");
    setParams(next);
  };
  const Switch = (
    <span className="seg" role="group" aria-label={t("View")}>
      <button type="button" aria-pressed={!detail} onClick={() => setDetail(false)}>{t("Simple")}</button>
      <button type="button" aria-pressed={detail} onClick={() => setDetail(true)}>{t("More detail")}</button>
    </span>
  );
  if (detail) {
    return (
      <div>
        <div className="toolbar"><span className="muted" style={{ fontSize: 13 }}>{t("Every column, for planners.")}</span><span className="spacer" />{Switch}</div>
        <ForecastDetail />
      </div>
    );
  }
  return <ForecastSimple switchEl={Switch} />;
}

function ForecastSimple({ switchEl }: { switchEl: React.ReactNode }) {
  const s = useSupply();
  const { open } = usePanel();
  const [params, setParams] = useSearchParams();
  const asked = params.get("type") as FType | null;
  const type: FType = asked && (TYPES as readonly string[]).includes(asked) ? asked : "Frame";
  const [pick, setPick] = useState<StatusKey | "all" | "ok">(params.get("show") === "need" || params.get("show") === "risk" ? "order" : "all");
  const [line, setLine] = useState<string | null>(null); // a product line picked from the "everything else" chips
  const [filter, setFilter] = useState("");
  const setType = (x: FType) => {
    const next = new URLSearchParams(params);
    next.set("type", x);
    setParams(next);
    setLine(null);
    setPick("all");
  };

  const yd = isYards(type);
  const unit = yd ? "yd" : "units";
  const unitText = t(unit);
  const all = useMemo(() => s.rows.filter((r) => r.item_type === type), [s.rows, type]);
  const counts = useMemo(() => Object.fromEntries(TYPES.map((x) => [x, s.rows.filter((r) => r.item_type === x).length])), [s.rows]);

  const view = useMemo(() => {
    const words = filter.toLowerCase().split(/\s+/).filter(Boolean);
    const withStatus = all.filter((r) => !isQuiet(r)).map((r) => ({ r, st: statusOf(r, s.today), g: groupOf(r) }));
    const tallies: Record<string, number> = { all: withStatus.length, ok: withStatus.filter((x) => !x.st.length).length };
    for (const x of withStatus) for (const y of x.st) tallies[y.key] = (tallies[y.key] ?? 0) + 1;
    tallies.attention = withStatus.filter((x) => isProblem(x.st)).length;
    let rows = withStatus;
    if (words.length) rows = rows.filter(({ r }) => words.every((w) => `${r.itemid} ${partName(r.itemid)}`.toLowerCase().includes(w)));
    if (pick === "ok") rows = rows.filter((x) => !x.st.length);
    else if (pick !== "all") rows = rows.filter((x) => x.st.some((y) => y.key === pick));
    if (line) rows = rows.filter((x) => x.g.key === line);
    // "Needs attention first" without a line picked = the attention list plus a summary of everything else.
    const attentionMode = pick === "all" && !line && !words.length;
    const list = attentionMode ? rows.filter((x) => isProblem(x.st)) : [...rows];
    if (attentionMode || pick !== "all") list.sort((a, b) => runOutDay(a.r) - runOutDay(b.r));
    else list.sort((a, b) => rowRank(a.r) - rowRank(b.r) || a.r.itemid.localeCompare(b.r.itemid));
    const rest = attentionMode ? rows.filter((x) => !isProblem(x.st)) : [];
    const restLines = new Map<string, { label: string; n: number; rank: number }>();
    for (const x of rest) {
      const cur = restLines.get(x.g.key) ?? { label: x.g.label, n: 0, rank: rowRank(x.r) };
      cur.n++;
      cur.rank = Math.min(cur.rank, rowRank(x.r));
      restLines.set(x.g.key, cur);
    }
    const lineLabel = line ? withStatus.find((x) => x.g.key === line)?.g.label ?? line : null;
    return {
      tallies, list, attentionMode, rest, lineLabel, quiet: all.length - withStatus.length,
      restLines: [...restLines.entries()].sort((a, b) => a[1].rank - b[1].rank),
    };
  }, [all, s.today, filter, pick, line]);

  const grouped = type === "Frame" || type === "Cover" || type === "Base";
  const tagLines = type === "Frame" || type === "Base"; // cover names already say Solstice / Pacifica
  const exportCsv = () =>
    downloadCsv(`forecast-${type}-${new Date().toISOString().slice(0, 10)}.csv`, [...view.list, ...view.rest].map(({ r, st, g }) => ({
      "Product line": g.label,
      Part: r.itemid,
      Name: partName(r.itemid),
      Status: st.map((x) => x.label).join(", ") || "OK",
      "Runs out (est.)": runOutDay(r) < 1e9 ? addDays(s.today, Math.floor(runOutDay(r))) : "",
      "On hand": Math.round(r.supply.comp_available ?? 0),
      "On order": Math.round(onOrder(r)),
      "Expected use, next 90 days": Math.round(r.demand?.forecasted ?? 0),
      "Need to order": Math.round(r.need_to_order ?? 0),
      Unit: unit,
    })));


  const title = view.attentionMode
    ? t("Needs attention · {n} parts", { n: view.list.length })
    : view.lineLabel
      ? `${view.lineLabel} · ${view.list.length}`
      : `${view.list.length} ${t(CATEGORY_LABEL[type]).toLowerCase()}`;

  const strip: Array<[StatusKey | "all" | "ok", string, string]> = [
    ["all", t("Needs attention"), ""],
    ["order", t("Order now"), "order"],
    ["sooner", t("Ship sooner"), "sooner"],
    ["late", t("Late PO"), "late"],
    ["topup", t("Top-up"), ""],
    ["over", t("Over a year of stock"), ""],
    ["ok", t("OK"), ""],
  ];

  return (
    <div className="u-page">
      <CategoryTabs items={TYPES} value={type} onChange={setType} label={(x) => t(CATEGORY_LABEL[x])} counts={counts} loading={s.loading} right={switchEl} />

      <div className="strip" role="group" aria-label={t("Show")}>
        <span className="strip-unit">{t("Parts")}</span>
        {strip.map(([k, label, cls]) => (
          <button key={k} type="button" className={cls} aria-pressed={pick === k}
            onClick={() => { setPick(pick === k && k !== "all" ? "all" : k); setLine(null); }}>
            <b>{s.loading ? "…" : k === "all" ? view.tallies.attention : view.tallies[k] ?? 0}</b>{label}
          </button>
        ))}
      </div>

      <ListBar title={title} search={filter} onSearch={setFilter}>
        {view.lineLabel && <button type="button" className="btn sm" onClick={() => setLine(null)}>{t("✕ Back to needs attention")}</button>}
        <ExportButton onClick={exportCsv} />
      </ListBar>

      {s.error ? (
        <div className="u-error">{t("Couldn't load the forecast:")} {s.error.message}</div>
      ) : s.loading ? (
        <div className="tablewrap"><div className="u-empty">{t("Loading…")}</div></div>
      ) : (
        <div className="tablewrap">
          <table className="dt fc-simple">
            <thead>
              <tr>
                <th>{t("Part")}</th>
                <th className="l">{t("Status")}</th>
                <th className="nb-h"><i className="stock" />{br(t("Current\nstock"))}</th>
                <th className="nb-h"><i className="est" />{t("Forecast use")}<br /><span className="sub">{t("red = more than stock")}</span></th>
                <th className="nb-h hide-m"><i className="order" />{t("On order")}<br /><span className="sub">{t("not here yet")}</span></th>
                <th className="l">{t("Runs out")}<br /><span className="sub">{t("at the forecast rate")}</span></th>
                <th>{br(t("Need to\norder"))}</th>
              </tr>
            </thead>
            <tbody>
              {view.list.length === 0 && (
                <tr><td colSpan={7} className="l muted">{view.attentionMode ? t("Nothing needs attention in this category.") : t("Nothing matches.")}</td></tr>
              )}
              {view.list.map(({ r, st, g }) => {
                const shown = st.filter((x) => x.key !== "over");
                return (
                  <tr key={r.itemid} className="click" onClick={() => open("part", r.itemid)}>
                    <td className="l">
                      <span className="code">{r.itemid}</span>
                      {tagLines && !view.lineLabel && <span className="line-tag">{g.label}</span>}
                      {partName(r.itemid) && <span className="name">{partName(r.itemid)}</span>}
                    </td>
                    <td className="l st">
                      {shown.length
                        ? <>
                            {shown.map((x) => <span key={x.key} className={`badge ${x.cls}`}>{x.label}</span>)}
                            {shown.every((x) => x.key === "late") && <span className="st-sub">{t("chase it · no order needed")}</span>}
                          </>
                        : <span className="ok">{st.length ? t("OK · 1 yr+ of stock") : t("OK")}</span>}
                    </td>
                    {(() => {
                      const f = forecastTriple(r);
                      const max = Math.max(f.onHand, f.use, 1); // one scale per row: stock vs use
                      return (
                        <>
                          <td className="nb-c"><StockNum v={f.onHand} max={max} /></td>
                          <td className="nb-c"><UseNum use={f.use} stock={f.onHand} max={max} /></td>
                          <td className="nb-c hide-m"><OrderNum r={r} max={max} /></td>
                        </>
                      );
                    })()}
                    <td className="l"><RunsOut r={r} /></td>
                    <td>{(r.need_to_order ?? 0) > 0 ? <b>{fmt(r.need_to_order)}</b> : <span className="muted">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {view.attentionMode && view.rest.length > 0 && (
        <div className="rest">
          <span className="rest-t">{t("Everything else · {n}", { n: view.rest.length })}</span>
          {grouped
            ? view.restLines.map(([k, l]) => (
                <button key={k} type="button" className="chip" onClick={() => setLine(k)}>{l.label} <b>{l.n}</b></button>
              ))
            : <button type="button" className="chip" onClick={() => setPick("ok")}>{t("Show them")}</button>}
          <span className="muted rest-n">{grouped ? t("Top-ups and OK parts. Pick a line to list its parts.") : t("Top-ups and OK parts.")}</span>
        </div>
      )}
      <p className="fine">
        {t("Quantities in {unit}. Click a part for its stock chart and how the order is worked out.", { unit: unitText })}
        {view.quiet > 0 && ` ${t("{n} parts with no stock, orders or forecast aren't listed.", { n: view.quiet })}`}
      </p>
    </div>
  );
}

/** A two-line column header: the translation keeps its own line break. */
const br = (s: string) => s.split("\n").flatMap((x, i) => (i ? [<br key={i} />, x] : [x]));

const runOutDay = (r: Row) => (r.days_cover_on_hand == null || !Number.isFinite(r.days_cover_on_hand) ? 1e9 : r.days_cover_on_hand);



/** When the shelf runs out (counting deliveries), and whether stock comes back. */
function RunsOut({ r }: { r: Row }) {
  const st = stockStory(r);
  if (st.tone === "none") return <span className="muted">—</span>;
  if (st.tone === "ok" || st.tone === "low") return <span className="muted">{t("6+ months")}</span>;
  return (
    <span className="ro">
      <b className="late-t">{st.outDate}</b>
      <span className="ro-s">{st.then}</span>
    </span>
  );
}
